/**
 * useYotpo Hook
 * 
 * React hook for managing Yotpo widget initialization and lifecycle.
 * Handles script loading, widget initialization, and cleanup.
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import {
  loadYotpoScript,
  initializeYotpoWidgets,
  isYotpoReady,
  getYotpoState,
} from '../utils/yotpoLoader';

/**
 * Custom hook for Yotpo integration
 * @param {Object} options - Configuration options
 * @param {string} options.appKey - Yotpo app key (required)
 * @param {boolean} options.autoInit - Auto-initialize widgets when ready (default: true)
 * @param {number} options.initDelay - Delay before initializing widgets in ms (default: 100)
 * @param {number} options.scriptTimeout - Script load timeout in ms (default: 10000)
 * @param {boolean} options.reinitOnMount - Reinitialize widgets on every mount (default: true)
 * @returns {Object} Yotpo state and methods
 */
export const useYotpo = (options = {}) => {
  const {
    appKey,
    autoInit = true,
    initDelay = 100,
    scriptTimeout = 10000,
    reinitOnMount = true,
  } = options;

  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  const initTimeoutRef = useRef(null);
  const mountedRef = useRef(true);

  /**
   * Initialize Yotpo widgets
   */
  const initWidgets = useCallback(() => {
    if (!isYotpoReady()) {
      console.warn('Yotpo not ready - cannot initialize widgets');
      return false;
    }

    try {
      // Clear any pending initialization
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }

      // Delay initialization to ensure DOM is ready
      initTimeoutRef.current = setTimeout(() => {
        const success = initializeYotpoWidgets();
        if (mountedRef.current) {
          setInitialized(success);
        }
      }, initDelay);

      return true;
    } catch (err) {
      console.error('Error initializing Yotpo widgets:', err);
      return false;
    }
  }, [initDelay]);

  /**
   * Reinitialize widgets (useful after route changes)
   */
  const reinitWidgets = useCallback(() => {
    if (loaded && isYotpoReady()) {
      setInitialized(false);
      initWidgets();
    }
  }, [loaded, initWidgets]);

  /**
   * Load Yotpo script
   */
  useEffect(() => {
    if (!appKey) {
      const error = new Error('Yotpo app key is required');
      setError(error);
      console.error(error.message);
      return;
    }

    mountedRef.current = true;
    setLoading(true);

    loadYotpoScript(appKey, { timeout: scriptTimeout })
      .then(() => {
        if (!mountedRef.current) return;
        
        setLoaded(true);
        setLoading(false);
        setError(null);

        // Auto-initialize if enabled
        if (autoInit) {
          initWidgets();
        }
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        
        setError(err);
        setLoading(false);
        console.error('Failed to load Yotpo script:', err);
      });

    return () => {
      mountedRef.current = false;
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
    };
  }, [appKey, autoInit, scriptTimeout, initWidgets]);

  /**
   * Reinitialize on mount if specified
   */
  useEffect(() => {
    if (reinitOnMount && loaded && isYotpoReady()) {
      reinitWidgets();
    }
  }, [reinitOnMount, loaded, reinitWidgets]);

  return {
    // State
    loaded,
    loading,
    error,
    initialized,
    ready: loaded && isYotpoReady(),

    // Methods
    initWidgets,
    reinitWidgets,

    // Utilities
    getState: getYotpoState,
  };
};

/**
 * Hook for simple widget initialization after route changes
 * Lightweight version that only reinitializes existing widgets
 * 
 * @param {Object} options
 * @param {boolean} options.enabled - Enable reinitialization (default: true)
 * @param {number} options.delay - Delay before reinit in ms (default: 200)
 * @param {Array} options.dependencies - Additional dependencies to trigger reinit
 */
export const useYotpoReinit = (options = {}) => {
  const {
    enabled = true,
    delay = 200,
    dependencies = [],
  } = options;

  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Reinitialize widgets after delay
    timeoutRef.current = setTimeout(() => {
      if (isYotpoReady()) {
        initializeYotpoWidgets();
      }
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, delay, ...dependencies]);
};

export default useYotpo;
